import {inject, injectable, Request, RequestStatus, Employee, GlobalContainerTypes, Language} from '../..';
import {RequestsNormalization} from './requests-normalization.types';
import {
  FiltersParams,
  RawFiltersParams,
  WorkspaceRequestActions,
  WorkspaceRequestStatus,
  WorkspacesRequestsResponse,
} from '../requests-api';
import {EmployeesNormalization} from '@innowise-group/core';
import {pickBy} from 'lodash';
import {API_TO_INTERNAL_STATUS_MAP, INTERNAL_TO_API_STATUS_MAP} from './constants';

@injectable()
class RequestsNormalizationService implements RequestsNormalization {
  public static readonly type = GlobalContainerTypes.RequestsNormalization;

  @inject(GlobalContainerTypes.EmployeesNormalization) private employeesNormalization: EmployeesNormalization;
  @inject(GlobalContainerTypes.Language) private language: Language;

  constructor() {
    this.normalizeWorkspaceActionRequestFromApi = this.normalizeWorkspaceActionRequestFromApi.bind(this);
    this.normalizeWorkspaceRequestFromApi = this.normalizeWorkspaceRequestFromApi.bind(this);
    this.normalizeRequestStatusFromApi = this.normalizeRequestStatusFromApi.bind(this);
    this.normalizeRequestStatusToApi = this.normalizeRequestStatusToApi.bind(this);
    this.normalizeRequestsRawFilters = this.normalizeRequestsRawFilters.bind(this);
  }

  public normalizeWorkspaceActionRequestFromApi(workspaceActionRequest: WorkspaceRequestActions): {
    status: RequestStatus;
    officeManager: Employee;
  } {
    const {status, office_manager} = workspaceActionRequest;
    return {
      status: this.normalizeRequestStatusFromApi(status),
      officeManager: this.employeesNormalization.normalizeEmployeeFromApi(office_manager),
    };
  }

  public normalizeWorkspaceRequestFromApi(workspaceRequest: WorkspacesRequestsResponse): Request {
    const {
      id,
      occupation_start_date,
      occupation_end_date,
      status,
      employee,
      workspace,
      room,
      office,
      office_eng,
      office_manager,
      creation_time,
      floor,
      comment,
      office_id,
      room_id,
      floor_id,
      personal_request,
      manager_comment,
      //backend isn't ready yet, need to set default value
      location_eng = '',
      location = '',
      request_type,
    } = workspaceRequest;
    return {
      id: id && String(id),
      user: this.employeesNormalization.normalizeEmployeeFromApi(employee),
      office: {
        address: this.language.checkAppLanguage() ? `${location}, ${office}` : `${location_eng}, ${office_eng}`,
        id: office_id && String(office_id),
      },
      room: {number: room, id: room_id && String(room_id)},
      seat: {number: String(workspace?.number), id: String(workspace?.id)},
      range: {
        start: occupation_start_date && new Date(occupation_start_date),
        end: occupation_end_date && new Date(occupation_end_date),
      },
      status:
        status === 'In progress' &&
        occupation_end_date &&
        new Date(occupation_end_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
          ? 'canceled'
          : this.normalizeRequestStatusFromApi(status),
      comment: comment,
      managerComment: manager_comment,
      floor: {number: floor && String(floor), id: floor_id && String(floor_id)},
      officeManager: this.employeesNormalization.normalizeEmployeeFromApi(office_manager),
      created: creation_time && new Date(creation_time),
      personalRequest: personal_request,
      requestType: request_type,
    };
  }

  private normalizeRequestStatusFromApi(workspaceStatus: WorkspaceRequestStatus): RequestStatus {
    return API_TO_INTERNAL_STATUS_MAP[workspaceStatus];
  }

  private normalizeRequestStatusToApi(requestStatus: RequestStatus): WorkspaceRequestStatus {
    return INTERNAL_TO_API_STATUS_MAP[requestStatus];
  }

  public normalizeRequestsRawFilters(filters: RawFiltersParams): FiltersParams {
    const {locationId, officeId, employee, myRequests, limit, offset, status} = filters;
    const processedFilters = {
      workspace__room__floor__office__location_id: locationId,
      workspace__room__floor__office_id: officeId,
      employee,
      my_requests: myRequests,
      status: status
        ? Array.isArray(status)
          ? status.map((s) => this.normalizeRequestStatusToApi(s))
          : [this.normalizeRequestStatusToApi(status)]
        : undefined,
      limit,
      offset,
    };
    return pickBy(processedFilters, (value) => Boolean(value));
  }
}

export default RequestsNormalizationService;
